import { useCallback, useRef, useState } from 'react';

import { useAttachmentSignedUrl } from 'hooks/useAttachmentSignedUrl';

/**
 * Fetches a signed view-only URL for an attachment without opening a window.
 * Use to embed the attachment as an iframe (e.g., a citation hover preview).
 *
 * Returns `{ url, fetch }`. `url` is null until `fetch()` has been called and
 * the request has resolved — and goes back to null while a refetch is in
 * flight or after one fails, so a cached URL that may have outlived the ~30s
 * server-side signature is never handed out. Call `fetch()` every time the
 * URL is about to be used (each hover/open): each call past the first
 * re-requests a fresh URL, deduped while one is already in flight. Skip the
 * call when the consumer no longer needs the URL (e.g., bytes already
 * cached) to avoid pointless requests.
 */
export function useAttachmentUrl(attachmentId: string, attachableModel?: string) {
  const [shouldFetch, setShouldFetch] = useState(false);
  const refetchInFlight = useRef(false);

  const { data, isFetching, isError, refetch } = useAttachmentSignedUrl(attachmentId, attachableModel, {
    enabled: shouldFetch,
    staleTime: 0,
  });

  const fetch = useCallback(() => {
    if (!shouldFetch) {
      // enabling the query triggers the initial fetch (staleTime 0 forces a
      // network request even when cached data exists)
      setShouldFetch(true);
    } else if (!isFetching && !refetchInFlight.current) {
      refetchInFlight.current = true;
      refetch().finally(() => {
        refetchInFlight.current = false;
      });
    }
  }, [shouldFetch, isFetching, refetch]);

  return {
    url: isFetching || isError ? null : (data?.url ?? null),
    fetch,
  };
}
